<template>
  <div class="mobile-sidebar-root">
    <!-- Dark Backdrop Overlay -->
    <div
      v-show="isOpen"
      class="sidebar-overlay"
      :class="{ 'is-active': isOpen }"
      @click="closeSidebar"
    ></div>

    <!-- Clean Compact Mobile Drawer Sidebar -->
    <aside class="app-sidebar" :class="{ 'is-open': isOpen }">
      <!-- Top Search & Close Bar -->
      <div class="sidebar-top-bar">
        <form @submit.prevent="handleSearch" class="sidebar-search-form">
          <i class="fas fa-search search-icon"></i>
          <input
            v-model="searchQuery"
            type="search"
            placeholder="Search products..."
            class="sidebar-search-input"
          />
        </form>
        <button @click="closeSidebar" class="sidebar-close-btn" aria-label="Close menu">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Compact Category Menu Items with Click Down/Up Toggle -->
      <nav class="sidebar-content" aria-label="Mobile navigation">
        <div class="menu-items-group">
          <!-- Fallback if categories are empty -->
          <template v-if="parsedCategories.length === 0">
            <a href="/trending" class="sidebar-menu-row" @click="closeSidebar">
              <span class="menu-row-label">Trending</span>
              <i class="fas fa-chevron-right menu-row-arrow"></i>
            </a>
          </template>

          <!-- Compact Category Rows with Down Chevron Toggle -->
          <div
            v-for="category in parsedCategories"
            :key="category.slug"
            class="category-item-block"
          >
            <!-- Category Header Row -->
            <div
              class="sidebar-menu-row"
              @click="handleCategoryClick(category)"
            >
              <span class="menu-row-label">{{ category.name }}</span>
              <div class="menu-row-right">
                <button
                  v-if="category.subcategories && category.subcategories.length > 0"
                  type="button"
                  class="toggle-chevron-btn"
                  aria-label="Toggle subcategories"
                >
                  <i
                    class="fas"
                    :class="expandedCategory === category.slug ? 'fa-chevron-up' : 'fa-chevron-down'"
                  ></i>
                </button>
                <i v-else class="fas fa-chevron-right menu-row-arrow"></i>
              </div>
            </div>

            <!-- Subcategories (Collapsed by Default, Opens on Click Down) -->
            <transition name="accordion">
              <div
                v-if="category.subcategories && category.subcategories.length > 0 && expandedCategory === category.slug"
                class="subcategories-accordion-list"
              >
                <a
                  v-for="sub in category.subcategories"
                  :key="sub.slug"
                  :href="sub.link"
                  class="sidebar-sub-row"
                  @click="closeSidebar"
                >
                  <span class="sub-row-label">{{ sub.name }}</span>
                  <i class="fas fa-chevron-right sub-row-arrow"></i>
                </a>
              </div>
            </transition>
          </div>
        </div>
      </nav>
    </aside>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  categories: {
    type: [Array, String, Object],
    default: () => []
  }
})

const isOpen = ref(false)
const searchQuery = ref('')
const expandedCategory = ref(null) // All categories start closed by default

const parsedCategories = computed(() => {
  let list = props.categories || []
  if (typeof list === 'string') {
    try {
      list = JSON.parse(list)
    } catch (e) {
      list = []
    }
  }
  if (!Array.isArray(list)) {
    if (typeof list === 'object' && list !== null) {
      list = Object.values(list)
    } else {
      list = []
    }
  }

  return list.map(cat => {
    const rawSubs = cat.subCategories || cat.sub_categories || cat.children || cat.subcategories || cat.subs || []
    const subList = Array.isArray(rawSubs) ? rawSubs : (typeof rawSubs === 'object' && rawSubs !== null ? Object.values(rawSubs) : [])
    
    return {
      id: cat.id || cat.slug || Math.random(),
      name: cat.name || cat.title || 'Category',
      slug: cat.slug || (cat.name ? String(cat.name).toLowerCase().replace(/\s+/g, '-') : 'category'),
      link: `/${cat.slug || ''}`,
      subcategories: subList.map(sub => ({
        id: sub.id || sub.slug || Math.random(),
        name: sub.name || sub.title || 'Subcategory',
        slug: sub.slug || (sub.name ? String(sub.name).toLowerCase().replace(/\s+/g, '-') : 'sub'),
        link: `/${cat.slug || ''}/${sub.slug || ''}`
      }))
    }
  })
})

const handleCategoryClick = (category) => {
  if (category.subcategories && category.subcategories.length > 0) {
    if (expandedCategory.value === category.slug) {
      expandedCategory.value = null // Close if already open
    } else {
      expandedCategory.value = category.slug // Open clicked category
    }
  } else {
    closeSidebar()
    window.location.href = category.link
  }
}

const closeSidebar = () => {
  isOpen.value = false
  expandedCategory.value = null // Reset accordions on close
  document.body.style.overflow = ''
}

const toggleSidebar = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    expandedCategory.value = null // Start with all accordions closed
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

const handleSearch = () => {
  if (searchQuery.value.trim()) {
    closeSidebar()
    window.location.href = `/search?q=${encodeURIComponent(searchQuery.value.trim())}`
  }
}

onMounted(() => {
  window.addEventListener('toggle-sidebar', toggleSidebar)
  window.addEventListener('open-sidebar', () => {
    isOpen.value = true
    expandedCategory.value = null
    document.body.style.overflow = 'hidden'
  })
  window.addEventListener('close-sidebar', closeSidebar)
})

onUnmounted(() => {
  window.removeEventListener('toggle-sidebar', toggleSidebar)
  document.body.style.overflow = ''
})
</script>

<style scoped>
/* ==========================================================================
   CLEAN COMPACT MOBILE SIDEBAR (START CLOSED, CLICK DOWN TO TOGGLE)
   ========================================================================== */

/* Hide completely on Desktop */
@media (min-width: 869px) {
  .mobile-sidebar-root {
    display: none !important;
  }
  .sidebar-overlay {
    display: none !important;
  }
  .app-sidebar {
    display: none !important;
  }
}

/* --- Mobile Overlay Backdrop --- */
.sidebar-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background: rgba(0, 0, 0, 0.75) !important;
  backdrop-filter: blur(4px) !important;
  -webkit-backdrop-filter: blur(4px) !important;
  z-index: 999998 !important;
  transition: opacity 0.3s ease;
}

/* --- Sidebar Drawer --- */
.app-sidebar {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 85vw !important;
  max-width: 320px !important;
  height: 100vh !important;
  height: 100dvh !important;
  background: #141414 !important;
  color: #ffffff !important;
  z-index: 999999 !important;
  display: flex !important;
  flex-direction: column !important;
  box-shadow: 6px 0 28px rgba(0, 0, 0, 0.6) !important;
  transform: translateX(-100%) !important;
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1) !important;
  overflow: hidden !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif !important;
}

.app-sidebar.is-open {
  transform: translateX(0) !important;
}

/* --- Top Search & Close Bar --- */
.sidebar-top-bar {
  display: flex !important;
  align-items: center !important;
  gap: 0.65rem !important;
  padding: 14px 16px 12px !important;
  background: #141414 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
  flex-shrink: 0 !important;
}

.sidebar-search-form {
  position: relative !important;
  flex: 1 !important;
  display: flex !important;
  align-items: center !important;
}

.search-icon {
  position: absolute !important;
  left: 12px !important;
  color: rgba(255, 255, 255, 0.5) !important;
  font-size: 0.85rem !important;
  pointer-events: none !important;
}

.sidebar-search-input {
  width: 100% !important;
  height: 38px !important;
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.16) !important;
  border-radius: 9999px !important;
  padding: 0 14px 0 34px !important;
  font-size: 0.88rem !important;
  color: #ffffff !important;
  outline: none !important;
  box-sizing: border-box !important;
  transition: border-color 0.2s ease, background 0.2s ease !important;
}

.sidebar-search-input::placeholder {
  color: rgba(255, 255, 255, 0.45) !important;
}

.sidebar-search-input:focus {
  background: rgba(255, 255, 255, 0.12) !important;
  border-color: #ffffff !important;
}

.sidebar-close-btn {
  width: 34px !important;
  height: 34px !important;
  border-radius: 50% !important;
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  color: #ffffff !important;
  font-size: 0.95rem !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
  flex-shrink: 0 !important;
  transition: background 0.2s ease, transform 0.2s ease !important;
}

.sidebar-close-btn:active {
  transform: scale(0.92) !important;
  background: rgba(255, 255, 255, 0.18) !important;
}

/* --- Scrollable Content --- */
.sidebar-content {
  flex: 1 !important;
  overflow-y: auto !important;
  padding: 6px 16px 36px !important;
  -webkit-overflow-scrolling: touch !important;
}

.sidebar-content::-webkit-scrollbar {
  width: 4px;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}

/* --- Menu Items Group --- */
.menu-items-group {
  display: flex !important;
  flex-direction: column !important;
}

.category-item-block {
  display: flex !important;
  flex-direction: column !important;
}

/* Compact Category Row */
.sidebar-menu-row {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 11px 0 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
  text-decoration: none !important;
  color: #ffffff !important;
  cursor: pointer !important;
  transition: opacity 0.2s ease !important;
}

.sidebar-menu-row:active {
  opacity: 0.75 !important;
}

.menu-row-label {
  font-size: 0.92rem !important;
  font-weight: 700 !important;
  color: #ffffff !important;
  letter-spacing: 0.02em !important;
  line-height: 1.2 !important;
}

.menu-row-right {
  display: flex !important;
  align-items: center !important;
}

.toggle-chevron-btn {
  background: transparent !important;
  border: none !important;
  color: rgba(255, 255, 255, 0.6) !important;
  font-size: 0.75rem !important;
  padding: 4px 6px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
}

.menu-row-arrow {
  color: rgba(255, 255, 255, 0.35) !important;
  font-size: 0.75rem !important;
}

/* --- Subcategories Accordion (Compact & Clean) --- */
.subcategories-accordion-list {
  display: flex !important;
  flex-direction: column !important;
  padding: 4px 0 6px 12px !important;
  background: rgba(255, 255, 255, 0.03) !important;
  border-radius: 6px !important;
  margin: 4px 0 6px !important;
}

.sidebar-sub-row {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 8px 8px 8px 0 !important;
  text-decoration: none !important;
  color: rgba(255, 255, 255, 0.75) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03) !important;
}

.sidebar-sub-row:last-child {
  border-bottom: none !important;
}

.sub-row-label {
  font-size: 0.85rem !important;
  font-weight: 500 !important;
  color: rgba(255, 255, 255, 0.75) !important;
}

.sub-row-arrow {
  color: rgba(255, 255, 255, 0.3) !important;
  font-size: 0.68rem !important;
}
</style>
